import React, { useState, useEffect } from 'react'
import { useMedications } from '../../hooks/useMedications'
import ActivityHistory from './ActivityHistory'

interface DayDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  selectedDate: Date | null
}

interface DayStats {
  totalScheduled: number
  totalTaken: number
  totalMissed: number
  adherencePercentage: number
  scheduledMedications: Array<{
    id: string
    name: string
    times: string[]
    taken: boolean
  }>
}

const DayDetailsModal: React.FC<DayDetailsModalProps> = ({ isOpen, onClose, selectedDate }) => {
  const [dayStats, setDayStats] = useState<DayStats | null>(null)
  const [loading, setLoading] = useState(false)
  
  const { getDayActivities, medications, isMedicationActiveOnDate, shouldTakeOnDate } = useMedications()

  const loadDayStats = async () => {
    if (!selectedDate) return
    
    setLoading(true)
    try {
      // Get activities for the selected day (not today)
      const dayActivities = await getDayActivities(selectedDate)
      
      // Get medications that should be taken on this specific day
      // Filter by both active on date (createdAt check) and schedule (shouldTakeOnDate)
      const dayMedications = medications.filter(med => 
        isMedicationActiveOnDate(med, selectedDate) && shouldTakeOnDate(med, selectedDate)
      )
      
      // Build scheduled medications with taken status based on day activities
      const scheduledMedications = []
      let totalScheduled = 0
      let totalTaken = 0
      
      for (const medication of dayMedications) {
        const times = medication.times || []
        
        if (times.length === 0) {
          // No specific times, check if taken at all on this day
          const taken = dayActivities.some(activity =>
            (activity.type === 'medication_taken' || activity.status === 'taken') &&
            activity.medicationId === medication.id
          )
          scheduledMedications.push({
            id: medication.id,
            name: medication.name,
            times: ['Sin horario específico'],
            taken
          })
          totalScheduled += 1
          if (taken) totalTaken += 1
        } else {
          for (const time of times) {
            const taken = dayActivities.some(activity =>
              (activity.type === 'medication_taken' || activity.status === 'taken') &&
              activity.medicationId === medication.id &&
              activity.scheduledTime === time
            )
            scheduledMedications.push({
              id: medication.id,
              name: medication.name,
              times: [time],
              taken
            })
            totalScheduled += 1
            if (taken) totalTaken += 1
          }
        }
      }
      
      const totalMissed = totalScheduled - totalTaken
      const adherencePercentage = totalScheduled === 0 ? 100 : (totalTaken / totalScheduled) * 100
      
      setDayStats({
        totalScheduled,
        totalTaken,
        totalMissed,
        adherencePercentage,
        scheduledMedications
      })
    } catch (error) {
      console.error('Error loading day stats:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && selectedDate) {
      loadDayStats()
    }
  }, [isOpen, selectedDate, medications])

  const formatDate = (date: Date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Hoy'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer'
    } else {
      return date.toLocaleDateString('es-ES', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    }
  }

  const formatTime = (time: string) => {
    if (time === 'Sin horario específico') return time
    
    const [hours, minutes] = time.split(':')
    const hour24 = parseInt(hours)
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    const ampm = hour24 >= 12 ? 'PM' : 'AM'
    return `${hour12}:${minutes} ${ampm}`
  }

  const getAdherenceColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400'
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400'
    return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400'
  }

  if (!isOpen || !selectedDate) return null

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Detalles del día - {formatDate(selectedDate)}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        ) : dayStats ? (
          <div className="space-y-6">
            {/* Day Summary */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Resumen del Día
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {dayStats.totalScheduled}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Programados
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {dayStats.totalTaken}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Tomados
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {dayStats.totalMissed}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Perdidos
                  </div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${getAdherenceColor(dayStats.adherencePercentage).split(' ')[0]}`}>
                    {Math.round(dayStats.adherencePercentage)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Adherencia
                  </div>
                </div>
              </div>

              {/* Adherence bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${
                    dayStats.adherencePercentage >= 80 ? 'bg-green-500' :
                    dayStats.adherencePercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${dayStats.adherencePercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Scheduled Medications */}
            {dayStats.scheduledMedications.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Medicamentos Programados
                </h3>
                
                <div className="space-y-3">
                  {dayStats.scheduledMedications.map((med, index) => (
                    <div key={`${med.id}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${med.taken ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {med.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {formatTime(med.times[0])}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        {med.taken ? (
                          <span className="flex items-center text-green-600 dark:text-green-400 text-sm">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Tomado
                          </span>
                        ) : (
                          <span className="flex items-center text-red-600 dark:text-red-400 text-sm">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Perdido
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity History for this day */}
            <ActivityHistory selectedDate={selectedDate} limit={50} />
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p>No hay información disponible para este día</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DayDetailsModal 